import React, { useState } from "react";
import { loginSeller } from "../api";
import { useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { showErrorToast, showSuccessToast } from "../utils/toaster";
import SellerForgotPasswordForm from "./forgotpassword";

function SignInForm() {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!credentials.email.trim()) newErrors.email = "Email is required.";
    if (!credentials.password.trim()) newErrors.password = "Password is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    let usercredentials = {};
    try {
      const response = await loginSeller(credentials);

      if (response.status === 200) {
        const token = response.data?.token;
        const sellerId = response.data?.seller?.id;
        let data = response.data;

        if (token && sellerId) {
          usercredentials.name = data.seller.name;
          localStorage.setItem("root", JSON.stringify(usercredentials));
          localStorage.setItem("token", token);
          localStorage.setItem("sellerId", sellerId);

          const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
          const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

          if (!data.seller.mobile || data.seller.mobile.trim() === "") {
            showErrorToast("Please complete your profile by adding your mobile number.");
            setTimeout(() => navigate("/seller/profile"), 1000);
          } else if (data.seller.gstNumber && !gstRegex.test(data.seller.gstNumber)) {
            showErrorToast("Invalid GST number. Please correct it in your profile.");
            setTimeout(() => navigate("/seller/profile"), 1000);
          } else if (data.seller.pan && !panRegex.test(data.seller.pan)) {
            showErrorToast("Invalid PAN number. Please correct it in your profile.");
            setTimeout(() => navigate("/seller/profile"), 1000);
          } else {
            showSuccessToast("Login successful! Redirecting to your dashboard...");
            setTimeout(() => navigate("/seller"), 1000);
          }
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        showErrorToast(
          error.response.data.message === "Account not verified"
            ? "Your account is not verified. Please verify your email."
            : "Payment is not completed. Please complete the payment to proceed."
        );
      } else if (error.response && error.response.status === 400) {
        showErrorToast("Invalid credentials. Please check your email and password.");
      } else {
        showErrorToast("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    setIsForgotPasswordOpen(true);
  };

  const handleResetSuccess = () => {
    setIsForgotPasswordOpen(false);
  };

  return (
    <>
      {isForgotPasswordOpen ? (
        <SellerForgotPasswordForm onResetSuccess={handleResetSuccess} />
      ) : (
        <>
          <p className="text-lg text-center text-gray-600 mb-6">
            Welcome back Seller ! Enter your details here to sign in.
          </p>

          <form
            className="flex flex-col justify-center items-center w-full"
            onSubmit={handleSubmit}
          >
            <div className="form-group w-full mb-4">
              <label htmlFor="email" className="block text-gray-700">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={credentials.email}
                onChange={handleChange}
                placeholder="Enter your email"
                className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div className="form-group w-full mb-4">
              <label htmlFor="password" className="block text-gray-700">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  errors.password ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            <button
              type="submit"
              className={`w-full bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <FaSpinner className="animate-spin text-2xl text-white mr-2" />
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <div className="text-center mt-4">
            <button
              onClick={handleForgotPassword}
              className="text-blue-500 hover:underline focus:outline-none"
            >
              Forgot Password?
            </button>
          </div>
        </>
      )}
    </>
  );
}

export default SignInForm;
